import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, ShieldCheck, X } from 'lucide-react';
import { MirrorReflector } from './branding.jsx';

let stopActiveLens = null;

export default function MirrorLens({ children = null, className = '', prompt = false, overlay = null, label = 'Open live Mirror Lens' }) {
  const [active, setActive] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState('');
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const mountedRef = useRef(true);
  const requestRef = useRef(0);

  const stop = useCallback(() => {
    requestRef.current += 1;
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setActive(false);
    setRequesting(false);
    if (stopActiveLens === stop) stopActiveLens = null;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stop();
    };
  }, [stop]);

  useEffect(() => {
    const stopWhenHidden = () => {
      if (document.visibilityState === 'hidden') stop();
    };
    window.addEventListener('pagehide', stop);
    document.addEventListener('visibilitychange', stopWhenHidden);
    return () => {
      window.removeEventListener('pagehide', stop);
      document.removeEventListener('visibilitychange', stopWhenHidden);
    };
  }, [stop]);

  useEffect(() => {
    if (!active || !videoRef.current || !streamRef.current) return;
    videoRef.current.srcObject = streamRef.current;
    void videoRef.current.play().catch(() => {
      stop();
      setError('Reflector™ could not start the preview, so the camera was stopped. Tap to try again.');
    });
  }, [active, stop]);

  const start = async event => {
    event.preventDefault();
    event.stopPropagation();
    setError('');
    if (active || requesting) {
      stop();
      return;
    }
    if (!window.isSecureContext) {
      setError('Reflector™ needs a secure HTTPS connection before camera access can begin.');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('This browser does not offer Reflector™ camera access here.');
      return;
    }
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setRequesting(true);
    try {
      stopActiveLens?.();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1280 } },
      });
      if (!mountedRef.current || requestRef.current !== requestId) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      stream.getVideoTracks().forEach(track => track.addEventListener('ended', stop, { once: true }));
      streamRef.current = stream;
      stopActiveLens = stop;
      setActive(true);
    } catch (cameraError) {
      if (requestRef.current !== requestId) return;
      const denied = cameraError?.name === 'NotAllowedError' || cameraError?.name === 'SecurityError';
      setError(denied ? 'Reflector™ was not given camera permission. You can allow it in this site’s browser settings.' : 'Reflector™ could not open the front camera.');
    } finally {
      if (mountedRef.current && requestRef.current === requestId) setRequesting(false);
    }
  };

  const keyDown = event => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    void start(event);
  };

  return <span className={`mirror-lens ${active ? 'is-live' : ''} ${requesting ? 'is-requesting' : ''} ${error ? 'has-error' : ''} ${className}`.trim()}>
    <span className="mirror-lens-saved">{children}</span>
    {active ? <video ref={videoRef} className="mirror-lens-video" autoPlay muted playsInline disablePictureInPicture aria-label="Live mirrored front camera; on-device preview only" /> : null}
    {overlay ? <span className="mirror-lens-overlay" aria-hidden="true">{overlay}</span> : null}
    {active ? <span className="mirror-lens-live" role="status"><i /><span>Reflector™ live</span><em>on device</em></span> : null}
    {prompt && !active ? <span className="mirror-lens-prompt" aria-hidden="true"><MirrorReflector /><strong>Reflector™</strong><small><ShieldCheck /> Private live view</small><em>No audio · no recording</em></span> : null}
    <span className="mirror-lens-control" role="button" tabIndex="0" aria-label={active || requesting ? 'Stop Reflector camera' : label} title={error || (active ? 'Stop the live camera and return to the saved image' : requesting ? 'Cancel camera request' : 'Open a private, on-device live mirror')} onClick={start} onKeyDown={keyDown}>{active || requesting ? <X /> : <><MirrorReflector /><Camera /></>}</span>
    {error ? <span className="mirror-lens-error" role="status">{error}</span> : null}
  </span>;
}
